<template>
    <Modal ref="modal"
           icon="fa-user"
           title="CUSTOMER_DETAILS_MODAL.MODAL_TITLE"
           size="lg"
           :ok="ok"
           :cancel="cancel"
           :ok-disabled="submitting"
           :ok-spinner="submitting"
           ok-label="CUSTOMER_DETAILS.OK">
        <form ref="formElement"
              @submit.prevent="ok">
            <ErrorMessage v-if="error"
                          :error="error" />
            <div v-else-if="validateForGiftAid"
                 class="alert alert-info"
                 data-testid="customer-form-info">
                <span class="fa-solid fa-info-circle"></span>
                {{ l('CUSTOMER_DETAILS.GIFT_AID_INFO') }}
            </div>

            <div class="form-h">
                <div v-if="company && company.shouldCaptureTitles()"
                     class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.TITLE') }}
                    </label>
                    <div class="form-group__input">
                        <input ref="titleInput"
                               v-model="internalCustomer.contact.title"
                               type="text"
                               class="form-control input-lg input-title"
                               data-testid="customer-form-title"
                               :placeholder="l('CUSTOMER_DETAILS.TITLE')"
                               autocomplete="no-auto-fill" />
                    </div>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.NAME') }}
                    </label>
                    <div class="form-group__input">
                        <input ref="firstNameInput"
                               v-model="internalCustomer.contact.firstName"
                               type="text"
                               class="form-control input-lg input-first-name"
                               data-testid="customer-form-first-name"
                               :placeholder="l('CUSTOMER_DETAILS.FIRST_NAME')"
                               autocomplete="no-auto-fill" />
                        <input v-model="internalCustomer.contact.lastName"
                               type="text"
                               class="form-control input-lg input-last-name"
                               data-testid="customer-form-last-name"
                               :placeholder="l('CUSTOMER_DETAILS.LAST_NAME')"
                               autocomplete="no-auto-fill" />
                    </div>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.EMAIL') }}
                    </label>
                    <div class="form-group__input">
                        <input ref="emailInput"
                               v-model="internalCustomer.contact.email"
                               type="email"
                               class="form-control input-lg input-email"
                               data-testid="customer-form-email"
                               :placeholder="l('CUSTOMER_DETAILS.EMAIL')"
                               autocomplete="no-auto-fill" />
                    </div>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.PHONE') }}
                    </label>
                    <div class="form-group__input">
                        <input v-model="internalCustomer.contact.tel"
                               type="tel"
                               class="form-control input-lg"
                               data-testid="customer-form-phone"
                               :placeholder="l('CUSTOMER_DETAILS.TELEPHONE')"
                               autocomplete="no-auto-fill" />
                    </div>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.ADDRESS') }}
                    </label>
                    <div class="form-h__right">
                        <AddressForm :address="internalCustomer.address" />
                    </div>
                </div>

                <div v-if="isOnline"
                     class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.CREATE_ACCOUNT__LABEL') }}
                    </label>
                    <div class="form-group__checkboxes">
                        <label class="form-group__checkbox">
                            <input v-model="formData.createAccount"
                                   type="checkbox"

                                   data-testid="customer-form-create-account" />
                            {{ l('CUSTOMER_DETAILS.CREATE_ACCOUNT') }}
                        </label>
                    </div>
                </div>

                <div v-if="isOnline && formData.createAccount"
                     ref="accountTypeSection"
                     class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.ACCOUNT_TYPE') }}
                    </label>
                    <div class="form-group__checkboxes">
                        <label class="form-group__checkbox">
                            <input v-model="formData.type"
                                   type="radio"
                                   name="type"
                                   value="personal"
                                   data-testid="customer-form-account-type-personal" />
                            <i class="fa-solid fa-user"></i>
                            {{ l('CUSTOMER_DETAILS.TYPE_PERSONAL') }}
                        </label>
                        <label class="form-group__checkbox">
                            <input v-model="formData.type"
                                   type="radio"
                                   name="type"
                                   value="business"
                                   data-testid="customer-form-account-type-business" />
                            <i class="fa-solid fa-briefcase"></i>
                            {{ l('CUSTOMER_DETAILS.TYPE_BUSINESS') }}
                        </label>
                    </div>
                </div>

                <div v-if="isOnline && formData.createAccount && formData.type === 'business'"
                     class="form-group">
                    <label class="control-label">
                        {{ l('CUSTOMER_DETAILS.ORGANISATION') }}
                    </label>
                    <div class="form-group__input">
                        <input ref="organisationInput"
                               v-model="formData.organisation"
                               type="text"
                               class="form-control input-lg input-organisation"
                               data-testid="customer-form-organisation"
                               :placeholder="l('CUSTOMER_DETAILS.ORGANISATION')"
                               autocomplete="no-auto-fill" />
                    </div>
                </div>
            </div>
            <div class="clearfix"></div>
        </form>
    </Modal>
</template>

<script>
import AddressForm from '@/epos/vue/components/AddressForm/AddressForm';
import CustomerAccount from '@/lib/Customers/CustomerAccount';
import CustomerAccountType from '@/lib/Customers/CustomerAccountType';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    components: {
        ErrorMessage,
        Modal,
        AddressForm,
    },

    mixins: [ModalPropsMixin],

    props: {
        customer: {
            type: Object,
            required: true,
        },

        validateForGiftAid: {
            type: Boolean,
            default: false,
        },

        /**
         * @type {?Error}
         */
        initialError: {
            type: Object,
            default: null,
        },

        createAccount: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            /**
             * @type {?Error}
             */
            error: this.initialError,

            // Clone of the customer object so we don't modify the original until submission.
            internalCustomer: this.customer,

            formData: {
                createAccount: this.createAccount,
                type: CustomerAccountType.PERSONAL,
                organisation: null,
            },

            submitting: false,
        };
    },

    computed: {
        ...mapState(['isOnline']),

        company() {
            return container.userService.company;
        },
    },

    watch: {
        'formData.createAccount': function (newValue) {
            if (newValue) {
                // When checking the box to create an account, scroll down so that the account type section is visible.
                // This often gets cut off the bottom of the modal.
                if (this.formData.type === CustomerAccountType.BUSINESS) {
                    // If the selected type is already business, focus the organisation input.
                    this.focusOrganisationInput();
                } else {
                    // Otherwise, focus the account type section.
                    this.focusAccountTypeInput();
                }
            }
        },

        'formData.type': function (newValue) {
            if (newValue === CustomerAccountType.BUSINESS) {
                // When you select a business account, focus the organisation input.
                this.focusOrganisationInput();
            }
        },
    },

    mounted() {
        this.initialize();
    },

    methods: {
        initialize() {
            // Set the customer's country to the company's country if one is not already set.
            if (!this.internalCustomer.address.country && this.company && this.company.country) {
                this.internalCustomer.address.country = this.company.country;
            }

            // Auto-focus the first input (title or first name depending on company settings).
            this.$nextTick(() => {
                if (this.company && this.company.shouldCaptureTitles()) {
                    this.$refs.titleInput?.focus();
                } else {
                    this.$refs.firstNameInput?.focus();
                }
            });
        },

        async ok() {
            // Prevent double submission
            if (this.submitting) {
                return;
            }

            // Set up account if creating one
            if (this.formData.createAccount && this.isOnline) {
                this.internalCustomer.account = new CustomerAccount();
                this.internalCustomer.account.type = this.formData.type;
                if (this.formData.type === CustomerAccountType.BUSINESS) {
                    this.internalCustomer.account.organisation = this.formData.organisation;
                }
            } else {
                this.internalCustomer.account = null;
            }

            // Validate
            if (this.validate() !== true) {
                // Scroll to top to show error message
                this.$nextTick(() => {
                    this.$refs.formElement?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                });
                return;
            }

            // Ask marketing preferences
            try {
                await this.askMarketingPreferences();
            } catch (error) {
                // User cancelled marketing preferences modal
                return;
            }

            // Submit
            this.submitAndClose();
        },

        validate() {
            let valid = true;
            const errors = [];

            this.error = null;

            // Validate email address if entered
            if (this.internalCustomer.contact.email && this.$refs.emailInput) {
                if (!this.$refs.emailInput.validity.valid) {
                    errors.push(container.langService.getText('CUSTOMER_DETAILS.EMAIL_INVALID'));
                    valid = false;
                }
            }

            // If creating an account, require one piece of contact info
            if (this.formData.createAccount && this.isOnline) {
                // If the company requires titles, and the title is not entered, then generate an error.
                if (this.company && this.company.shouldCaptureTitles() && !this.internalCustomer.contact.title) {
                    errors.push(
                        container.langService.getText('CUSTOMER_DETAILS.REQUIRED_FOR_ACCOUNT', {
                            field: 'CUSTOMER_DETAILS.TITLE',
                        })
                    );
                    valid = false;
                }

                // Always first name & last name
                if (!this.internalCustomer.contact.firstName) {
                    errors.push(
                        container.langService.getText('CUSTOMER_DETAILS.REQUIRED_FOR_ACCOUNT', {
                            field: 'CUSTOMER_DETAILS.FIRST_NAME',
                        })
                    );
                    valid = false;
                }

                if (!this.internalCustomer.contact.lastName) {
                    errors.push(
                        container.langService.getText('CUSTOMER_DETAILS.REQUIRED_FOR_ACCOUNT', {
                            field: 'CUSTOMER_DETAILS.LAST_NAME',
                        })
                    );
                    valid = false;
                }

                if (!this.validateForGiftAid) {
                    /**
                     * At least one of:
                     * tel
                     * email
                     * address (house, postcode)
                     */

                    if (
                        !this.internalCustomer.contact.email
                        && !this.internalCustomer.contact.tel
                        && !(this.internalCustomer.address.house && this.internalCustomer.address.postcode)
                    ) {
                        errors.push(container.langService.getText('CUSTOMER_DETAILS.REQUIRED_CONTACT_FOR_ACCOUNT'));
                        valid = false;
                    }

                    if (
                        this.internalCustomer.account
                        && this.internalCustomer.account.type === 'business'
                        && !this.internalCustomer.account.organisation
                    ) {
                        errors.push(container.langService.getText('CUSTOMER_DETAILS.ORG_REQUIRED_FOR_BUSINESS'));
                        valid = false;
                    }
                }

                if (this.validateForGiftAid) {
                    if (
                        !this.internalCustomer.contact.firstName
                        || !this.internalCustomer.contact.lastName
                        || !this.internalCustomer.address.house
                        || !this.internalCustomer.address.postcode
                    ) {
                        errors.push(container.langService.getText('CUSTOMER_DETAILS.GIFT_AID_CUSTOMER_INFO_INVALID'));
                        valid = false;
                    }
                }
            }

            if (!valid) {
                this.error = new Error(errors.join(' '));
                return false;
            }

            return true;
        },

        async askMarketingPreferences() {
            if (!this.internalCustomer.contact.email) {
                // No email entered. No preferences to be collected.
                return null;
            }

            if (this.internalCustomer.contact.marketingPreferences !== null) {
                // Already asked.
                return null;
            }

            const marketingQuestions = await container.marketingPreferenceAsker.getQuestions();
            if (marketingQuestions.length < 1) {
                // No questions.
                return null;
            }

            // Ask the marketing questions by opening the CustomerMarketingPreferencesModal.
            // This handles either asking on the customer screen or falling back to the operator answering.
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.CUSTOMER_MARKETING_PREFERENCES,
                    {
                        questions: marketingQuestions,
                    }
                )
                .then(
                    /**
                     * @param {number[]} marketingPreferenceIDs
                     */
                    (marketingPreferenceIDs) => {
                        this.internalCustomer.contact.marketingPreferences = marketingPreferenceIDs;
                    }
                );
        },

        submitAndClose() {
            if (this.formData.createAccount && this.isOnline) {
                this.submitting = true;
                container.customerAccountService.save(
                    this.internalCustomer.toServerData(),
                    (response, customerAccount) => {
                        if (customerAccount) {
                            this.resolve({
                                customerAccount,
                            });
                        } else if (response.error) {
                            this.error = new Error(response.error);
                        }
                        this.submitting = false;
                    },
                    (response) => {
                        this.error = new Error(response.data.error);
                        this.submitting = false;
                    }
                );
            } else {
                this.resolve({
                    customer: this.internalCustomer,
                });
            }
        },

        focusAccountTypeInput() {
            this.$nextTick(() => {
                // Originally this would use this.$refs.accountTypeSection.scrollIntoView({ behavior: 'smooth' });
                // but that didn't look as nice because then the radios would be tight against the bottom of the modal.
                // Since this will be the last visible element, we can just scroll to the bottom of the modal for
                // a nicer effect.
                this.scrollToBottom();
            });
        },

        focusOrganisationInput() {
            this.$nextTick(async () => {
                // Originally this would use this.$refs.organisationInput.scrollIntoView({ behavior: 'smooth' });
                // but that didn't look as nice because it would end up with half of the input visible.
                // Since this will be the last visible element, we can just scroll to the bottom of the modal for
                // a nicer effect.
                this.scrollToBottom();

                // Wait for the scroll to finish.
                // We could use an event listener, but that is overkill and was error-prone when tested.
                await timeoutPromise(150);

                // Then focus the input.
                // If we focused immediately the scoll position would not be where we wanted it to be.
                this.$refs.organisationInput?.focus();
            });
        },

        scrollToBottom() {
            this.$refs.modal?.$refs['modal-body']?.scrollTo({
                top: this.$refs.modal.$refs['modal-body'].scrollHeight,
                behavior: 'smooth',
            });
        },
    },
};
</script>
